import React, { useEffect, useState } from 'react';
import { invoiceAPI, accountAPI, clientPaymentsAPI } from '../lib/api';
import { formatINR, formatDate, todayISO, apiError } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import DatePicker from './DatePicker';
import ClientPicker from './ClientPicker';
import { toast } from 'sonner';
import { Loader2, CircleDollarSign } from 'lucide-react';

export const PAYMENT_METHODS = ['Cash', 'UPI', 'Bank Transfer', 'Cheque'];

const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;

const emptyForm = () => ({ payment_date: todayISO(), method: '', account_id: '', reference: '', notes: '' });

// Open = issued, not settled, not cancelled.
const isOpen = (i) => !['draft', 'paid', 'cancelled'].includes(i.status) && (Number(i.balance_due) || 0) > 0.01;

// One receipt that settles several invoices of one client.
// Without `client`, pass `clients` and the dialog asks which client first.
export default function ReceivePaymentDialog({ open, onOpenChange, client, clients, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]); // open invoices, oldest first
  const [accounts, setAccounts] = useState([]);
  const [alloc, setAlloc] = useState({}); // invoice id -> { on: bool, amt: string }
  const [received, setReceived] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [pickedId, setPickedId] = useState('');

  useEffect(() => { if (!open) setPickedId(''); }, [open]);

  const active = client || clients?.find((c) => c.id === pickedId);
  const clientId = active?.id;

  useEffect(() => {
    if (!open || !clientId) return undefined;
    let live = true;
    setLoading(true);
    setInvoices([]);
    setAlloc({});
    setReceived('');
    setForm(emptyForm());
    Promise.all([invoiceAPI.getAll(), accountAPI.getAll()])
      .then(([iRes, aRes]) => {
        if (!live) return;
        const rows = (iRes.data || [])
          .filter((i) => i.client_id === clientId && isOpen(i))
          .sort((a, b) => (a.issued_at || '').localeCompare(b.issued_at || '')
            || String(a.invoice_number).localeCompare(String(b.invoice_number), undefined, { numeric: true }));
        setInvoices(rows);
        // A lone open invoice is pre-ticked; with several, the user picks (or types Amount received).
        setAlloc(Object.fromEntries(rows.map((i) => [i.id, { on: rows.length === 1, amt: String(round2(Number(i.balance_due) || 0)) }])));
        setAccounts((aRes.data || []).filter((a) => a.status !== 'closed'));
      })
      .catch((err) => {
        if (live) toast.error(apiError(err, "Couldn't load this client's open invoices"));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, clientId]);

  const setRow = (id, patch) => setAlloc((a) => ({ ...a, [id]: { ...a[id], ...patch } }));

  const toggleRow = (inv, on) => {
    const cur = alloc[inv.id] || { amt: '' };
    setRow(inv.id, { on, amt: on && !(parseFloat(cur.amt) > 0) ? String(round2(Number(inv.balance_due) || 0)) : cur.amt });
  };

  // Typing "Amount received" fills the invoices oldest-first, each up to its balance.
  const onReceived = (v) => {
    setReceived(v);
    let left = round2(parseFloat(v) || 0);
    if (left <= 0) return;
    setAlloc(Object.fromEntries(invoices.map((i) => {
      const bal = round2(Number(i.balance_due) || 0);
      const a = Math.min(left, bal);
      left = round2(left - a);
      return [i.id, { on: a > 0, amt: String(a > 0 ? a : bal) }];
    })));
  };

  const picked = invoices
    .map((i) => ({ inv: i, amount: round2(parseFloat(alloc[i.id]?.amt) || 0), on: !!alloc[i.id]?.on }))
    .filter((r) => r.on && r.amount > 0);
  const allocTotal = round2(picked.reduce((s, r) => s + r.amount, 0));
  const recAmt = round2(parseFloat(received) || 0);
  const diff = recAmt > 0 ? round2(recAmt - allocTotal) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!picked.length) return toast.error('Allocate the payment to at least one invoice');
    const over = picked.find((r) => r.amount > round2(Number(r.inv.balance_due) || 0) + 0.01);
    if (over) return toast.error(`Amount for ${over.inv.invoice_number} is more than its balance due (${formatINR(over.inv.balance_due)}).`);
    if (!form.payment_date) return toast.error('Pick a payment date');
    setSaving(true);
    try {
      const res = await clientPaymentsAPI.record(clientId, {
        payment_date: form.payment_date,
        allocations: picked.map((r) => ({ invoice_id: r.inv.id, amount: r.amount })),
        account_id: form.account_id || null,
        reference: form.reference,
        notes: form.notes,
        method: form.method,
      });
      toast.success(`Receipt #${res.data?.payment_number} recorded`);
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(apiError(err, 'Failed to record payment'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="receive-payment-dialog">
        <DialogHeader>
          <DialogTitle>Receive payment</DialogTitle>
          <DialogDescription>
            {active
              ? <><span className="font-medium">{active.name}</span> · one receipt, settled against the open invoices below</>
              : 'Pick the client who paid. One receipt, settled against their open invoices.'}
          </DialogDescription>
        </DialogHeader>

        {!client && clients && (
          <div className="space-y-2">
            <Label>Client</Label>
            <ClientPicker clients={clients} value={pickedId} onChange={setPickedId} />
          </div>
        )}

        {!clientId ? null : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 py-6 text-center">This client has no open invoices.</p>
            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="recv-amount">Amount received (₹, optional)</Label>
                <Input
                  id="recv-amount"
                  data-testid="receive-amount-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={received}
                  onChange={(e) => onReceived(e.target.value)}
                  placeholder="Fills oldest invoices first"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recv-date">Payment date</Label>
                <DatePicker
                  id="recv-date"
                  value={form.payment_date}
                  onChange={(v) => setForm({ ...form, payment_date: v })}
                />
              </div>
            </div>

            {/* Open invoices, oldest first */}
            <div className="border rounded-md">
              <ul className="max-h-64 overflow-y-auto divide-y">
                {invoices.map((inv) => {
                  const row = alloc[inv.id] || { on: false, amt: '' };
                  return (
                    <li key={inv.id} className="flex items-center gap-3 px-3 py-2" data-testid={`receive-invoice-row-${inv.id}`}>
                      <Checkbox
                        id={`recv-chk-${inv.id}`}
                        data-testid={`receive-check-${inv.id}`}
                        checked={row.on}
                        onCheckedChange={(v) => toggleRow(inv, v === true)}
                      />
                      <label htmlFor={`recv-chk-${inv.id}`} className="min-w-0 flex-1 cursor-pointer">
                        <span className="block font-mono text-xs font-medium break-all">{inv.invoice_number}</span>
                        <span className="block text-xs text-gray-500">
                          Due {formatDate(inv.due_at)}{inv.status === 'overdue' ? ' · overdue' : ''} · Balance {formatINR(inv.balance_due)}
                        </span>
                      </label>
                      <Input
                        aria-label={`Amount to allocate to ${inv.invoice_number}`}
                        data-testid={`receive-alloc-${inv.id}`}
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.amt}
                        onChange={(e) => setRow(inv.id, { amt: e.target.value, on: (parseFloat(e.target.value) || 0) > 0 })}
                        className={`w-28 sm:w-36 h-8 text-right font-mono ${row.on ? '' : 'text-gray-400'}`}
                      />
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-center justify-between bg-gray-50 border-t px-3 py-2 text-sm">
                <span className="font-semibold">Total allocated · {picked.length} invoice{picked.length === 1 ? '' : 's'}</span>
                <span className="font-mono font-bold" data-testid="receive-total">{formatINR(allocTotal)}</span>
              </div>
            </div>
            {Math.abs(diff) > 0.01 && (
              <p className="text-xs text-gray-600">
                {diff > 0
                  ? `${formatINR(diff)} of the amount received is not allocated to any invoice.`
                  : `Allocations are ${formatINR(-diff)} more than the amount received.`}
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="recv-method">Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger id="recv-method" data-testid="receive-method">
                    <SelectValue placeholder="Pick a method (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="recv-account">Deposited to</Label>
                <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                  <SelectTrigger id="recv-account" data-testid="receive-account">
                    <SelectValue placeholder="Pick an account (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name} {a.last4 ? `· ••${a.last4}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="recv-ref">Reference / UTR (optional)</Label>
              <Input
                id="recv-ref"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. UTR123456789 or cheque #00045"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recv-notes">Notes (optional)</Label>
              <Textarea
                id="recv-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="receive-submit"
                className="bg-black hover:bg-black/90 text-white"
                disabled={saving || picked.length === 0}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CircleDollarSign className="w-4 h-4 mr-2" />}
                Record payment
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
